import { AppStack } from 'component/navigation/app-stack';
import { ScreenErrorBoundary } from 'component/navigation/screen-error-boundary';
import { useNeedsProfileSetup } from 'data/me';
import { useIsSuspended } from 'data/suspensions';
import { type Href, type RoutePath, router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useCallback, useMemo } from 'react';
import { useSiloLanguage } from 'system/request-metadata';
import { useSession } from 'util/auth-client';
import { useGlobalHrefParams } from 'util/navigate';

export const unstable_settings = {
    screenErrorBoundary: ScreenErrorBoundary,
};

export default function AppLayout() {
    const { siloKey } = useLocalSearchParams<Extract<RoutePath, `/[siloKey]/${string}`>>();
    const session = useSession();
    const profileKey = session.data?.user.id ?? null;
    const locale = useSiloLanguage();
    const isSuspended = useIsSuspended({ siloKey, profileKey, locale });
    const needsSetup = useNeedsProfileSetup({ siloKey, profileKey, locale });
    const globalParams = useGlobalHrefParams();
    const href = useMemo((): Href | null => {
        if (isSuspended) {
            return {
                pathname: '/[siloKey]/suspended',
                params: { ...globalParams, siloKey },
            };
        }

        if (needsSetup) {
            return {
                pathname: '/[siloKey]/profile',
                params: { ...globalParams, siloKey },
            };
        }

        return null;
    }, [globalParams, isSuspended, needsSetup, siloKey]);

    useFocusEffect(
        useCallback(() => {
            if (href) {
                // Pushing, not replacing, keeps the current page in history so
                // the pushed route can go back.
                router.push(href);
            }
        }, [href]),
    );

    return <AppStack />;
}
